// janus-engine/ability/armor-bending.ts — lane AB1 (2026-10-03, board R-125): the ARMOR BENDING ROLL in the browser, the twin of
// includes/functions/janus/core/armor_bending_roll.php (run by template_spell_roll.php before the counterspell check).
//
// PHP is the spec. A spell whose spell_sphere list is non-empty and holds no Divine sphere, with a somatic component
// (spell_component_somatic) or a material item (spell_material_item_id), cast by a creature wearing armor (an inventory row equipped in the
// Armor slot), rolls the armor's skill (Heavy / Medium / Light Armor Skill by the row's armor_type) as a skill roll of roll type
// `armor_bending_roll` against DC 5 + the armor's ac_mod, rolled by the caster to no one:
//   · total < DC  → FAILED: `armor_bending_roll_failed` (caster → caster, the spell's id); the spell does nothing — no counterspell check,
//                   no roll dispatch, no mana — but the action is spent (AP, the post-roll resource hook, an innate charge);
//   · total == DC → a wild-magic SURGE (random_table_roll on the surge table, logged by the caster to no one), then the spell goes on;
//   · total > DC  → the spell goes on.
// Only the FIRST equipped armor row rolls. An engine-bound Arcane Arrow release skips this roll before examining the archer's armor.
import { type JanusCtx, type Row, handBack, insertRoll, int, itemField, record, scalar, upper } from './context.js';
import { type JanusInput } from './janus.js';
import { selectSkillForContestingRoll } from './select-skill-for-contesting-roll.js';
import { skillRollArr } from './skill-roll.js';
import { readRandomTableDef, rollRandomTable } from '../rolls/random-table.js';
import { randomTableChainPreDraw, WILD_MAGIC_SURGE_TABLE_ID } from './random-table-payload.js';
import { arcaneArrowDeliveryMatches } from './cast-modifier-ports.js';

/** RollType armor_bending_roll (includes/additions.php). */
export const ARMOR_BENDING_ROLL = '9C752002-0C85-40D8-8DC0-D43AECBB2207';
const DIVINE_SPHERE = '57F117E8-6659-4F2D-AED2-82D6005492B9';
const ARMOR_SLOT = '0B279A55-5102-D704-8A57-0C1FF4989DFA';
/** ArmorType → ArmorSkill (armor_bending_roll()'s $armor_skill_map). */
const ARMOR_SKILL: Record<string, string> = {
  'F94A6EAD-BC5A-466D-B27B-7688423B4332': '937A5402-E933-4F87-8CAE-93427F47894A', // Heavy → Heavy Armor Skill
  'FEC16F4D-6309-48A5-82E4-833EF06A0E54': '24495AD8-8EB9-488E-B816-5513E0A3DAA8', // Medium → Medium Armor Skill
  'D33CD865-4B66-4843-9AE5-9AFCA1133253': '44F9404E-77AC-1F60-68A4-4CD6AE071161', // Light → Light Armor Skill
};

const uuidOf = (entry: unknown): string => upper(typeof entry === 'string' ? entry.split('|')[0] : itemField(record(entry), 'item_id') ?? record(entry)?.item_id);
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;

/** The spell's own gate (armor_bending_roll() before it looks at the caster): non-divine sphere AND (somatic OR material item). */
function spellBends(ctx: JanusCtx, ability: Row): boolean {
  const spheres = ctx.read.entries(ability, 'spell_sphere').map(uuidOf);
  return spheres.length > 0 && !spheres.includes(DIVINE_SPHERE)
    && (ctx.read.has(ability, 'spell_component_somatic') || ctx.read.has(ability, 'spell_material_item_id'));
}

/** The caster's first armor row equipped in the Armor slot, null when it wears none; hands back when the caster's gear is not provable. */
function equippedArmor(ctx: JanusCtx, caster: Row): Row | null {
  // layer_has($caster_arr, 'inventory'): a compiled caster without the layer carries nothing. A lean roster row (no compiled creature) proves
  // nothing about its gear.
  if (!record(record(caster.computed)?.creature) && !ctx.read.has(caster, 'inventory')) handBack('armor_bending_roll:inventory-not-held');
  for (const raw of ctx.read.entries(caster, 'inventory')) {
    const row = record(raw);
    if (!row) handBack('armor_bending_roll:inventory-row-not-held');
    const equipped = upper(scalar(ctx.read.inventoryField?.(row, 'equipped') ?? record(row.inventory)?.equipped));
    if (equipped === ARMOR_SLOT) return row;
  }
  return null;
}

/** Whether armor_bending_roll() rolls for this cast (no draw). Hands back on state the page cannot prove. */
export function armorBendingApplies(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): boolean {
  if (arcaneArrowDeliveryMatches(ctx, ability, input.useAbilityVariable2)) return false;
  if (!spellBends(ctx, ability)) return false;
  const armor = equippedArmor(ctx, caster);
  if (!armor) return false;
  return true;
}

/**
 * armor_bending_roll(): 'none' (no roll), 'failed' (the spell does nothing; the caller spends the action without mana) or 'passed' (incl. a
 * tie, whose surge is rolled + logged here). Every hand-back is decided before the first die.
 */
export function armorBendingRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): 'none' | 'failed' | 'passed' {
  if (!armorBendingApplies(ctx, input, ability, caster)) return 'none';
  const armor = equippedArmor(ctx, caster)!;
  // The encounter's inventory rows are thinned: their template layers can live
  // only in the held catalog. PHP's layer_value bridges the instance to its full
  // compile. Resolve that same held definition here, preserving instance overrides.
  // AB1 real-PHP oracle: Plate/Brigandine/Gambeson all rolled in PHP while these
  // lean rows previously handed back armor-type-missing despite held definitions.
  const templateId = upper(scalar(ctx.read.inventoryField?.(armor, 'inventory_item_id')
    ?? record(armor.inventory)?.inventory_item_id ?? itemField(armor, 'item_id')));
  const definition = ctx.read.definition(templateId);
  const armorValue = (key: string): unknown => ctx.read.value(armor, key)
    ?? (definition ? ctx.read.value(definition, key) : null);
  const armorType = upper(scalar(armorValue('armor_type')));
  // PHP display_warning + exit on a missing / unknown armor type: no row, no cast — the page cannot reproduce an exit.
  if (!UUID.test(armorType)) handBack('armor_bending_roll:armor-type-missing');
  const skillId = ARMOR_SKILL[armorType];
  if (!skillId) handBack('armor_bending_roll:armor-type-unknown');
  const acRaw = scalar(armorValue('ac_mod') ?? 0);
  const acMod = int(acRaw); // intval()
  const dc = 5 + acMod;
  // the tie's surge table must be held and rollable before the skill roll draws (counterspell's RT-4 pattern)
  randomTableChainPreDraw(ctx, WILD_MAGIC_SURGE_TABLE_ID);
  const skill = selectSkillForContestingRoll(ctx, caster, [skillId]);
  const total = skillRollArr(ctx, input, caster, skill, null, dc, { rollType: ARMOR_BENDING_ROLL });
  if (total < dc) {
    insertRoll(ctx, { roll_type_1: 'armor_bending_roll_failed', roll_item_id: upper(input.abilityId), by: caster, to: caster });
    return 'failed';
  }
  if (total === dc) {
    // random_table_roll() on the surge table with caster_arr = the caster: logged by the caster, to no one (the twin of counterspell's tie)
    const out = rollRandomTable(WILD_MAGIC_SURGE_TABLE_ID, id => readRandomTableDef(id, ctx.read.definition(upper(id))), die => ctx.roll.d(die));
    if (!out.ok) handBack('random_table_roll:surge-table-not-held'); // unreachable after randomTableChainPreDraw
    for (const step of out.result.steps) insertRoll(ctx, { roll_type_1: step.roll_type_1, roll_type_2: step.roll_type_2,
      roll_item_id: step.roll_item_id, by: caster, to: null, roll_result: step.roll_result, rolls_full: JSON.parse(step.rolls_full) as unknown[],
      dice: step.dice, die: step.die, mod: step.roll_mod, adv_dis_stacks: step.adv_dis_stacks, random_table: step.random_table });
  }
  return 'passed';
}
